import { useEffect, useMemo, useState } from 'react'
import {
  Image, Box, Text, Button, Collapse, Spinner, FormControl,
  FormLabel, Input, FormErrorMessage, Modal, ModalOverlay, Link,
  ModalContent, Checkbox, List, ListItem, Flex, Popover, PopoverTrigger, PopoverContent,
  PopoverArrow, PopoverCloseButton, PopoverHeader, PopoverBody, TableContainer,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from '@chakra-ui/react'
import { useSelector, useDispatch } from 'react-redux'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { containerWidth } from 'styles'
import useSearch from 'hooks/search'
import useTalent from 'hooks/talent'
import ClientSearch from 'components/ClientSearch'
import { setCurrentSearch, clearCurrentSearch } from 'store/modules/search'
import { listTalentsBySearch } from 'services'
import { API_URL } from 'constants'
import { colors } from 'styles'
import {
  FaCaretDown, FaCaretUp, FaCopy, FaEnvelope, FaEye, FaFilePdf,
  FaSortAlphaDown, FaSortAlphaUp
} from 'react-icons/fa'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation } from 'swiper'
import ClientSelect from 'components/ClientSelect'
import useUIContext from 'hooks/ui-context'
import Card from 'components/Card'
import SearchLinkHint from 'components/SearchLinkHint'
import { USER_TYPES } from 'constants'
import { calcAge, formatDate, getDatePart, getFileAge, getOptionLabel, getTalentSlug } from 'utils'
import TalentBookInfo from 'components/TalentBookInfo'
import NoPhotoImg from 'asset/no-photo.png'
import Pagination from "components/Pagination";

const SortIcons = {
  '0': null,
  '1': <FaSortAlphaDown />,
  '-1': <FaSortAlphaUp />
}

let loadTimeoutHandle = null

const SearchTalents = () => {
  const { currentSearch, getOneSearch, createSearch, updateSearch } = useSearch()
  const { updateTalentById } = useTalent()
  const { id: searchId } = useParams()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const homePath = location.pathname.split('/')[1]
  const {
    formState: { errors },
    control,
    handleSubmit,
    register,
    reset
  } = useForm()
  const { setBreadcrumbs, addNotification } = useUIContext()

  const enumData = useSelector(state => state.talent.enumData)
  const user = useSelector(state => state.user.currentUser)

  const [showBookInfo, setShowBookInfo] = useState(false)
  const [showFilter, setShowFilter] = useState(false)
  const [talents, setTalents] = useState([])
  const [loadingTalent, setLoadingTalent] = useState(false)
  const [savingSearch, setSavingSearch] = useState(false)
  const [currentTalent, setCurrentTalent] = useState(null)
  const [searchIdLoading, setSearchIdLoading] = useState(true)
  const [searchMode, setSearchMode] = useState('none');
  const [selectedTalents, setSelectedTalents] = useState([]);
  // const [deselectedTalents, setDeselectedTalents] = useState([]);
  
  const [totalCount, setTotalCount] = useState(0);
  const [pagination, setPagination] = useState({
    page: 0,
    perPage: 50,
    totalPages: 1,
  });

  const [sort, setSort] = useState({
    first_name: null,
    last_name: 1,
  })

  // Returns just the ids of every talent matching the search. The server sends
  // back an _id-only projection, so this stays cheap even across the full
  // roster — unlike fetching whole talent documents to read one field off them.
  const fetchAllTalentIds = async (search, selected_talents) => {
    const res = await listTalentsBySearch({
      // anonymous callers are pinned to this saved search server-side
      search_id: searchId && searchId !== 'new' ? searchId : null,
      ...search,
      selected_talents: !!(searchId && searchId === 'new') ? null : selected_talents,
      accepted_only: true,
      fields: 'ids'
    })
    return res.data.talents?.map(t => t._id) || []
  }

  const loadTalents = async (search, selected_talents) => {
    setLoadingTalent(true)
    try {
      const talentsRes = await listTalentsBySearch({
      // anonymous callers are pinned to this saved search server-side
      search_id: searchId && searchId !== 'new' ? searchId : null,
        ...search,
        selected_talents: !!(searchId && searchId === 'new') ? null : selected_talents,
        page_num: pagination.page,
        per_page: pagination.perPage,
        accepted_only: true,
        sort
      })
      setTalents(talentsRes.data.talents)
      setTotalCount(talentsRes.data.total_count)

      setPagination({
        ...pagination,
        totalPages: Math.ceil(talentsRes.data.total_count / pagination.perPage),
      });
      if (selectedTalents?.length === 0) {
        if (!!(searchId && searchId === 'new')) {
          setSearchMode('all')
          dispatch(setCurrentSearch({
            ...currentSearch,
            ...!!(searchId && searchId === 'new') && {
              selected_talents: selectedTalents,
              search_mode: searchMode
            }
          }))
          setSelectedTalents(await fetchAllTalentIds(search, selected_talents));
        } else {
          if (selected_talents?.length > 0) {
            setSearchMode('all');
            // Reconcile the saved selection against the saved filter. A search
            // saved after "Select All" stores the whole roster; if the filter is
            // narrowed later, that list goes stale and the UI reported its raw
            // length ("3,498 Selected" on a link containing 10 people). The ids
            // projection returns exactly the intersection, so this is cheap.
            setSelectedTalents(await fetchAllTalentIds(search, selected_talents));
          } else {
            setSearchMode('none');
            setSelectedTalents([]);
          }
        }
      }
    } catch (err) {
      addNotification(`Loading Talents Failed: ${err}`)
    }
    setLoadingTalent(false)
  }

  useEffect(() => {
    if (searchIdLoading) {
      return
    }
    if (loadTimeoutHandle) {
      clearTimeout(loadTimeoutHandle)
    }
    loadTimeoutHandle = setTimeout(() => {
      loadTalents(currentSearch.search, currentSearch.selected_talents)
    }, 300)
  }, [currentSearch.search, searchIdLoading, sort])

  useEffect(() => {
    if (!searchIdLoading) {
      loadTalents(currentSearch.search, currentSearch.selected_talents);
    }
  }, [pagination.page, pagination.perPage]);

  const handleSaveSearch = async (value) => {
    setSavingSearch(true)
    try {
      if (searchId && searchId !== 'new') {
        await updateSearch(searchId, {
          ...currentSearch,
          selected_talents: selectedTalents,
          ...value
        })
        addNotification({
          status: 'success',
          content: 'Saved'
        })
      } else {
        const searchRes = await createSearch({
          ...currentSearch,
          selected_talents: selectedTalents,
          ...value
        })
        if (searchRes._id) {
          navigate(`/${homePath}/dashboard/search/${searchRes._id}`)
        }

      }
      if (user && user.user_type === USER_TYPES.ADMIN) {
          navigate(`/${homePath}/dashboard/searches`)
      }
    } catch (err) {
      addNotification({
        status: 'error',
        content: `Failed to Save: ${err}`
      })
    }
    setSavingSearch(false)
  }

  useEffect(() => {
    if (!!(searchId && searchId === 'new')) {
      dispatch(setCurrentSearch({
        ...currentSearch,
        ...!!(searchId && searchId === 'new') && {selected_talents: selectedTalents},
        ...!!(searchId && searchId === 'new') && {_id: null}
      }))
    }
  }, [talents, searchId])

  useEffect(() => {
    const loadSearch = async () => {
      let pathLabel = 'Create New Search'
      if (searchId !== 'new') {
        setSearchIdLoading(true)
        const searchRes = await getOneSearch(searchId)

        dispatch(setCurrentSearch(searchRes))
        reset({
          owner: searchRes.owner,
          name: searchRes.name,
          description: searchRes.description
        })
        pathLabel = searchRes.name
      }
      setBreadcrumbs([{
        path: `/${homePath}/dashboard`,
        label: 'Dashboard'
      }, {
        path: `/${homePath}/dashboard/searches`,
        label: 'Searches'
      }, {
        path: `/${homePath}/dashboard/search/${searchId}`,
        label: pathLabel
      }])
      setSearchIdLoading(false)
    }
    loadSearch()
    return () => {
      dispatch(clearCurrentSearch())
      setSelectedTalents([])
      setTotalCount(0)
    }
  }, [searchId])

  const toggleTalentSelect = (talent) => {
    let talentIdsArray = []

    const idx = selectedTalents.findIndex(i => i === talent._id)
    if (idx > -1) {
      talentIdsArray = [...selectedTalents]
      talentIdsArray.splice(idx, 1)
    } else {
      talentIdsArray = [...selectedTalents, talent._id]
    }
    setSelectedTalents(talentIdsArray)
  }

  const sortedTalents = useMemo(() => {
    let result = talents
    if(
      !!currentSearch && currentSearch.selected_talents && currentSearch.selected_talents.length > 0 
      && !!searchId && searchId !== 'new'
    ) {
      result = result.filter(talent => currentSearch.selected_talents.includes(talent._id));
    }

    return result
  }, [talents, selectedTalents])

  const filteredBookouts = (talent) => {
    const filteredBookouts = talent?.bookouts?.filter(bookout => !!bookout.start_date);
    return filteredBookouts
  }

  const isModelSelected = (talentObj) => {
    return selectedTalents.includes(talentObj._id)
  }

  const sendEmail = async (search) => {
    const talentsRes = await listTalentsBySearch({
      // anonymous callers are pinned to this saved search server-side
      search_id: searchId && searchId !== 'new' ? searchId : null,
      ...search,
      selected_talents: selectedTalents,
      accepted_only: true,
      fields: 'emails'
    })

    const allSelectedTalents = talentsRes.data.talents.map(t => t.email)
    window.location.href = `mailto:?bcc=${allSelectedTalents.join(',')}`;
  }

  const onClickSelectUnSelectAll = async () => {
    if (searchMode === 'none') {
      setSearchMode('all');
      setSelectedTalents(await fetchAllTalentIds(currentSearch.search, currentSearch.selected_talents));
    } else if (searchMode === 'all') {
      setSearchMode('none');
      setSelectedTalents([]);
    }
  }

  return (
    <Box width={containerWidth} mx='auto' mb={12} px={{base: 2, sm: 0}}>
      <Box mb={5}>
        <Card
          bodyProps={{m: 0}} 
          title='Save Search'
        >
          <Box
            overflow='visible'
            px={0} py={4}
          >
            <form onSubmit={handleSubmit(handleSaveSearch)}>
              {user.user_type === USER_TYPES.ADMIN && (
                <FormControl isInvalid={errors.owner}>
                  <FormLabel>Client</FormLabel>
                  <ClientSelect
                    control={control}
                    name='owner'rules={{ required: 'This field is required' }}
                    defaultValue={currentSearch?.owner}
                  />
                  <FormErrorMessage>{(errors.owner && errors.owner.message)}</FormErrorMessage>
                </FormControl>
              )}
              <FormControl isInvalid={errors.name}>
                <FormLabel>Name</FormLabel>
                <Input {...register('name', { required: 'This field is required' })}/>
                <FormErrorMessage>{(errors.name && errors.name.message)}</FormErrorMessage>
              </FormControl>
              <FormControl>
                <FormLabel>Message</FormLabel>
                <Input {...register('description')}/>
              </FormControl>
              <Box mt={3}>
                <Button isLoading={savingSearch} disabled={savingSearch} type='submit'>Save</Button>
              </Box>
            </form>
          </Box>
        </Card>
      </Box>
      <Box mb={3}>
        <Card
          bodyProps={{m: 0}} title='Talent Filter'
          rightIcon={!showFilter ? <FaCaretDown />: <FaCaretUp />}
          onHeaderClick={() => { setShowFilter(!showFilter) }}
        >
          <Collapse in={showFilter} style={{ overflow: 'visible'}}>
            <Box
              overflow='visible'
              px={0} py={4}
            >
              <ClientSearch
                key={currentSearch._id}
                search={currentSearch.search}
                searchSubmit={search => {
                  setSelectedTalents([])
                  dispatch(setCurrentSearch({
                    ...currentSearch,
                    search
                  }))
                }}
              />
            </Box>
          </Collapse>
        </Card>
      </Box>
      <Box display='flex' flexDirection='column' gap={2} mb={3} ml={3} alignItems='center'>
        <Box display='flex' gap={6}>
          <Text>{totalCount} Total</Text>
          <Text>{selectedTalents?.length || 0} Selected</Text>
        </Box>
        <SearchLinkHint maxW="640px" />
        <Box display='flex' gap={2} flexWrap='wrap'>
          <Button variant='outline' onClick={onClickSelectUnSelectAll}>
            {searchMode === 'all' ? 'Unselect All' : 'Select All'}
          </Button>

          <Button onClick={() => {
            switch (sort.first_name) {
              case null:
                setSort({ ...sort, first_name: 1 })
                break
              case 1:
                setSort({ ...sort, first_name: -1 })
                break
              case -1:
                setSort({ ...sort, first_name: null })
                break
              default: 
                setSort({ ...sort, first_name: null })
                break
            }
          }} rightIcon={SortIcons[`${sort.first_name}`]}>
            First Name
          </Button>

          <Button onClick={() => {
            switch (sort.last_name) {
              case null:
                setSort({ ...sort, last_name: 1 })
                break
              case 1:
                setSort({ ...sort, last_name: -1 })
                break
              case -1:
                setSort({ ...sort, last_name: null })
                break
              default: 
                setSort({ ...sort, last_name: null })
                break
            }
          }} rightIcon={SortIcons[`${sort.last_name}`]}>
            Last Name
          </Button>
          
          {loadingTalent && (<Spinner />)}

          {(user.user_type === USER_TYPES.ADMIN) &&

            <Button
              leftIcon={<FaEnvelope />}
              onClick={async () => {
                await sendEmail(currentSearch.search)
              }}
            >
              Email to all
            </Button>
          }
        </Box>

      </Box>
      <Box display='flex' mt={3} alignItems='center' justifyContent='flex-end'>
        <Pagination pagination={pagination} setPagination={setPagination} perPageCountIncludes={['50', '100']} />

      </Box>
      <Box
        display='flex'
        flexWrap='wrap'
        justifyContent='space-around'
        gap={3}
        mt={5}
      >
        {sortedTalents.map(talent => {
          const selected = isModelSelected(talent)

          return (
            <Box
              w={44}
              key={talent._id}
              display='flex'
              flexDirection='column'
              p={3}
              borderRadius={5}
              alignItems='center'
              bgColor={selected ? colors.doubleDark : colors.light}
              position='relative'
            >
              {user.user_type === USER_TYPES.ADMIN && filteredBookouts(talent)?.length > 0 &&
                <Popover trigger='hover'>
                  <PopoverTrigger>
                    <Box
                      as='span'
                      position='absolute'
                      top={3}
                      right={3}
                      transform='translate(50%, -50%)'
                      bgColor='red'
                      borderRadius='50%'
                      width={4}
                      height={4}
                      _hover={{ cursor: 'pointer' }} // Optional: Change cursor on hover
                    />
                  </PopoverTrigger>
                  <PopoverContent minW={{ base: "100%", lg: "max-content" }}>
                    <PopoverArrow />
                    <PopoverCloseButton />
                    <PopoverHeader>Future Bookouts</PopoverHeader>
                    <PopoverBody>
                    <TableContainer>
                      <Table>
                        <Thead>
                          <Tr>
                            <Th>Start Date</Th>
                            <Th>End Date</Th>
                            <Th>Reason</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {filteredBookouts(talent)
                            ?.sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
                            ?.map((bookout, idx) => {
                            if (bookout !== "") {
                              return (
                                <Tr key={idx}>
                                  <Td>{formatDate(bookout.start_date)}</Td>
                                  <Td>{formatDate(bookout.end_date)}</Td>
                                  <Td>{bookout.note}</Td>
                                </Tr>
                              );
                            }
                            return null;
                          })}
                        </Tbody>
                      </Table>
                    </TableContainer>
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
              }
              {talent.photos[0] ? (
                <Box mt='auto' w='100%' mb={3} onClick={async () => {
                  if (user?.user_type === USER_TYPES.ADMIN) {
                    window.open(`/admin/dashboard/talent/edit/${talent._id}`);
                    await updateTalentById(talent._id, { is_viewed_by_admin: true, by_admin: true });
                    loadTalents(currentSearch.search, currentSearch.selected_talents);
                  } else if (user?.user_type === USER_TYPES.CLIENT) {	
                    window.open(`/talent/${getTalentSlug(talent)}`);
                  }
                }}>
                  <Image 
                    borderRadius={10}
                    overflow='hidden'
                    mx='auto'
                    src={`${API_URL}/s3/${encodeURIComponent('150x150/' + talent.photos[0])}`}
                    h={40}
                    flex={1}
                    objectFit='contain'
                    cursor='pointer'  // Ensures image appears clickable for all users
                  />
              </Box>
              ): <Box flex={1} onClick={async () => {
                if (user.user_type === USER_TYPES.ADMIN) {
                  window.open(`/admin/dashboard/talent/edit/${talent._id}`)
                  await updateTalentById(talent._id, { is_viewed_by_admin: true, by_admin: true })
                  loadTalents(currentSearch.search, currentSearch.selected_talents);
                }
              }} cursor={user.user_type === USER_TYPES.ADMIN? 'pointer': 'default'}>
                <Image
                    borderRadius={10}
                    src={NoPhotoImg}
                />
                <Text fontSize='xs' textAlign='center' color={selected ? colors.primaryText : colors.primary}>No photo available.</Text>
              </Box>}
              <Text
                mt='auto'
                color={selected ? colors.primaryText : colors.primary}
                textAlign='center'
                cursor='pointer'
                fontWeight={(talent.is_viewed_by_admin === false && searchId === 'new' && user.user_type === USER_TYPES.ADMIN) ? 'bold' : ''}
                onClick={() =>{	
                if (user.user_type === USER_TYPES.ADMIN) {	
                  setCurrentTalent(talent)	
                }	
                else if ((user.user_type === USER_TYPES.CLIENT)) {	
                  window.open(`/talent/${getTalentSlug(talent)}`)	

                }}}
              >{talent.stage_first_name && talent.stage_last_name
              ? `${talent.stage_first_name} ${talent.stage_last_name}`
              : `${talent.first_name} ${talent.last_name}`}</Text>
              <Text
                color={selected ? colors.primaryText : colors.primary}
                my={2} whiteSpace='nowrap' textAlign='center'
              >
                  {calcAge(talent)}
              </Text>

              <Button onClick={() => { toggleTalentSelect(talent) }}>
                {selected ? 'Unselect': 'Select'}
              </Button>
            </Box>
          )
        })}
      </Box>
      <Modal isOpen={!!currentTalent} onClose={() => setCurrentTalent(null) }>
        <ModalOverlay />
        {currentTalent && (
          <ModalContent>
            <Card
              title={`${currentTalent.first_name} ${currentTalent.last_name}`}
              bodyProps={{ width: '100%', px: 7 }}
            >
              <Box mb={3} display='flex' justifyContent='space-between' alignItems='center'>
                <Checkbox defaultChecked={selectedTalents.includes(currentTalent._id)} onChange={() => {
                  toggleTalentSelect(currentTalent)
                }}>
                  Talent is Selected
                </Checkbox>
                <Button
                  as={Link}
                  leftIcon={<FaEnvelope />}
                  href={`mailto:${currentTalent.email}`}
                  target='_blank'
                >
                  Email
                </Button>
              </Box>
              <Swiper navigation={true} modules={[Navigation]}>
                {currentTalent.photos.map(photo => {
                  return (
                    <SwiperSlide key={photo}>
                      <Box display='flex' alignItems='center' justifyContent='center' flexDirection='column'>
                        <Image
                          width={120}
                          borderRadius={5}
                          overflow='hidden'
                          src={`${API_URL}/s3/${encodeURIComponent(photo)}`}
                        />
                        <Text mt={3} whiteSpace='nowrap'>{getFileAge(photo)}</Text>
                      </Box>
                    </SwiperSlide>
                  )
                })}
              </Swiper>

              <List>
                <ListItem display='flex' justifyContent='space-between'>
                  <Text>Date of Birth:</Text>
                  <Text>{getDatePart(currentTalent.birth_date)}</Text>
                </ListItem>
                <ListItem display='flex' justifyContent='space-between'>
                  <Text>Eye Color:</Text>
                  <Text>{currentTalent.eye_color}</Text>
                </ListItem>
                <ListItem display='flex' justifyContent='space-between'>
                  <Text>Hair Color:</Text>
                  <Text>{currentTalent.hair_color}</Text>
                </ListItem>
                <ListItem display='flex' justifyContent='space-between'>
                  <Text>Height:</Text>
                  <Text>{getOptionLabel(currentTalent.height, enumData.height)}</Text>
                </ListItem>
                <ListItem display='flex' justifyContent='space-between'>
                  <Text>Weight</Text>
                  <Text>{getOptionLabel(currentTalent.weight, enumData.weight)}</Text>
                </ListItem>
                <ListItem display='flex' justifyContent='space-between'>
                  <Text>Clothing Size:</Text>
                  <Text>{getOptionLabel(currentTalent.cloth_size, enumData.cloth_size)}</Text>
                </ListItem>
                <ListItem display='flex' justifyContent='space-between'>
                  <Text>Shoe Size:</Text>
                  <Text>{getOptionLabel(currentTalent.shoe_size, enumData.shoe_size)}</Text>
                </ListItem>
                <ListItem display='flex' justifyContent='space-between'>
                  <Text>Ethnicities</Text>
                  <Text>{getOptionLabel(currentTalent.ethnicity_look, enumData.ethnicity, true)}</Text>
                </ListItem>
                <ListItem display='flex' justifyContent='space-between'>
                  <Text>Ethnicity Actual</Text>
                  <Text>{getOptionLabel(currentTalent.ethnicity_actual, enumData.ethnicity, true)}</Text>
                </ListItem>
                <ListItem display='flex' justifyContent='space-between'>
                  <Text>Union Status</Text>
                  <Text>{currentTalent.union_status}</Text>
                </ListItem>
                <ListItem display='flex' justifyContent='space-between'>
                  <Text>Bookouts:</Text>
                  <Box>
                    {filteredBookouts(currentTalent)?.map((bookout, idx) => {
                      return (
                        <Text key={idx}>
                          {formatDate(bookout.start_date)}~{formatDate(bookout.end_date)} : {bookout.note}
                        </Text>
                      )
                    })}
                    {filteredBookouts(currentTalent)?.length === 0 && (
                      'No bookouts'
                    )}
                  </Box>
                </ListItem>

              </List>
              <Box display='flex' mt={3} flexWrap='wrap' rowGap={2}>
                <Button leftIcon={<FaEye />} onClick={() => {
                  window.open(`/talent/${getTalentSlug(currentTalent)}`)
                }}>
                  View Full Profile
                </Button>
                {currentTalent.resume && (
                  <Button leftIcon={<FaFilePdf />} ml='auto' onClick={() => {
                    window.open(`${API_URL}/s3/${encodeURIComponent(currentTalent.resume)}`, '_blank')
                  }}>
                    Download Resume
                  </Button>
                )}
                <Button leftIcon={<FaCopy />} w='100%' onClick={() => {
                  setShowBookInfo(true)
                }}>
                  Generate Booking Information
                </Button>
              </Box>
            </Card>
          </ModalContent>
        )}
      </Modal>

      <Modal isOpen={currentTalent && showBookInfo} onClose={() => {
        setShowBookInfo(false)
      }} blockScrollOnMount={false}>
        <ModalOverlay />
        <ModalContent>
          {currentTalent && (
            <TalentBookInfo
              talent={currentTalent}
              close={() => {
                setShowBookInfo(false)
              }}
            />
          )}
        </ModalContent>
      </Modal>
    </Box>
  )
}

export default SearchTalents
